import React, { useEffect, useRef, useState } from 'react';

export const EXPAND_DURATION_MS = 200;

/**
 * Smoothly expands and collapses its content.
 *
 * Animating to and from `height: auto` is not possible in CSS, so the panel
 * measures its content and animates max-height between 0 and that measured
 * value. Once open it switches to `none`, otherwise content that grows or
 * reflows afterwards (a wrapped permission list, say) would be clipped.
 *
 * Tailwind here is 2.0.2, which has no arbitrary values, so the transition is
 * applied as an inline style rather than a utility class.
 */
export default function ExpandablePanel({
    open,
    children,
}: {
    open: boolean;
    children: React.ReactNode;
}) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState(open ? 'none' : '0px');

    useEffect(() => {
        const element = contentRef.current;
        if (!element) return undefined;

        if (open) {
            setMaxHeight(`${element.scrollHeight}px`);
            // Release the fixed height once the transition has finished.
            const timer = setTimeout(() => setMaxHeight('none'), EXPAND_DURATION_MS);
            return () => clearTimeout(timer);
        }

        // Collapsing needs a concrete starting height, so pin the current
        // height first and drop to zero on the next frame.
        setMaxHeight(`${element.scrollHeight}px`);
        const frame = requestAnimationFrame(() => setMaxHeight('0px'));
        return () => cancelAnimationFrame(frame);
    }, [open]);

    return (
        <div
            style={{
                maxHeight,
                overflow: 'hidden',
                transition: `max-height ${EXPAND_DURATION_MS}ms ease-in-out, opacity ${EXPAND_DURATION_MS}ms ease-in-out`,
                opacity: open ? 1 : 0,
            }}
        >
            <div ref={contentRef}>{children}</div>
        </div>
    );
}
